/**
 * IntroPicker — visual thumbnail grid letting the owner choose which
 * opening animation to use for their invitation. Includes a "Default"
 * option that uses the template-assigned variant.
 */

const VARIANTS = [
  { id: "default", label: "Default", subtitle: "Template's default" },
  { id: "envelope", label: "Envelope", subtitle: "Wax-sealed envelope opens" },
  { id: "curtain", label: "Curtain", subtitle: "Red velvet curtains part" },
  { id: "petals", label: "Petals", subtitle: "Rose petals shower" },
  { id: "sky", label: "Day & Night", subtitle: "Sunrise → starlight gradient" },
  { id: "compass", label: "Compass", subtitle: "Compass spins" },
  { id: "doors", label: "Doors", subtitle: "Twin doors swing open" },
  { id: "mandala", label: "Mandala", subtitle: "Mandala rings draw" },
  { id: "page", label: "Page Flip", subtitle: "Magazine page turns" },
  { id: "cake", label: "Cake", subtitle: "Confetti & cake reveal" },
  { id: "seal", label: "Wax Seal", subtitle: "Rotating wax seal" },
];

function Thumb({ variant, accent = "#B89B5E" }) {
  const s = "w-full h-full";
  switch (variant) {
    case "default":
      return <div className="w-full h-full flex items-center justify-center bg-stone-100 text-stone-500 text-[10px] uppercase tracking-[0.2em]">Auto</div>;
    case "envelope":
      return (
        <svg viewBox="0 0 100 70" className={s} aria-hidden="true">
          <rect width="100" height="70" fill="#FAF6EE" />
          <rect x="20" y="22" width="60" height="36" fill={accent} opacity="0.85" />
          <path d="M 20 22 L 50 44 L 80 22" fill="none" stroke="#FAF6EE" strokeWidth="2" />
        </svg>
      );
    case "curtain":
      return (
        <svg viewBox="0 0 100 70" className={s} aria-hidden="true">
          <rect width="100" height="70" fill="#1A0507" />
          <path d="M 0 0 Q 20 35 0 70 L 35 70 L 35 0 Z" fill="#7A1518" />
          <path d="M 100 0 Q 80 35 100 70 L 65 70 L 65 0 Z" fill="#7A1518" />
          <text x="50" y="42" textAnchor="middle" fill={accent} fontSize="14" fontFamily="serif" fontStyle="italic">A&amp;D</text>
        </svg>
      );
    case "petals":
      return (
        <svg viewBox="0 0 100 70" className={s} aria-hidden="true">
          <rect width="100" height="70" fill="#FCEFEC" />
          {[15, 30, 50, 65, 80].map((x, i) => (
            <ellipse key={i} cx={x} cy={i * 8 + 10} rx="3" ry="4" fill={accent} opacity="0.7" />
          ))}
          <text x="50" y="42" textAnchor="middle" fill={accent} fontSize="14" fontFamily="serif" fontStyle="italic">A❀D</text>
        </svg>
      );
    case "sky":
      return (
        <svg viewBox="0 0 100 70" className={s} aria-hidden="true">
          <defs><linearGradient id="sky-g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#FFE3C2" /><stop offset="0.5" stopColor="#E8A86A" /><stop offset="1" stopColor="#0E1330" /></linearGradient></defs>
          <rect width="100" height="70" fill="url(#sky-g)" />
          <circle cx="50" cy="20" r="8" fill="#FFB061" />
          <circle cx="50" cy="58" r="5" fill="#F0F4FA" />
          <text x="50" y="42" textAnchor="middle" fill="#fff" fontSize="11" fontFamily="serif" fontStyle="italic">A&amp;D</text>
        </svg>
      );
    case "compass":
      return (
        <svg viewBox="0 0 100 70" className={s} aria-hidden="true">
          <rect width="100" height="70" fill="#F4F1EB" />
          <circle cx="50" cy="35" r="22" fill="none" stroke={accent} strokeWidth="1" />
          <path d="M 50 14 L 53 35 L 50 56 L 47 35 Z" fill={accent} />
          <circle cx="50" cy="35" r="2" fill={accent} />
        </svg>
      );
    case "doors":
      return (
        <svg viewBox="0 0 100 70" className={s} aria-hidden="true">
          <rect width="100" height="70" fill="#FAF7F0" />
          <rect x="10" y="6" width="38" height="58" fill={accent} />
          <rect x="52" y="6" width="38" height="58" fill={accent} />
          <rect x="14" y="10" width="30" height="50" fill="none" stroke="#FAF7F0" strokeWidth="0.8" opacity="0.6" />
          <rect x="56" y="10" width="30" height="50" fill="none" stroke="#FAF7F0" strokeWidth="0.8" opacity="0.6" />
        </svg>
      );
    case "mandala":
      return (
        <svg viewBox="0 0 100 70" className={s} aria-hidden="true">
          <rect width="100" height="70" fill="#FAF6EE" />
          {[8, 14, 20, 26].map((r) => <circle key={r} cx="50" cy="35" r={r} fill="none" stroke={accent} strokeWidth="0.7" opacity="0.7" />)}
          {Array.from({ length: 12 }).map((_, i) => {
            const a = (i / 12) * Math.PI * 2;
            return <line key={i} x1={50 + Math.cos(a) * 8} y1={35 + Math.sin(a) * 8} x2={50 + Math.cos(a) * 26} y2={35 + Math.sin(a) * 26} stroke={accent} strokeWidth="0.4" />;
          })}
        </svg>
      );
    case "page":
      return (
        <svg viewBox="0 0 100 70" className={s} aria-hidden="true">
          <rect width="100" height="70" fill="#F8F4ED" />
          <text x="50" y="22" textAnchor="middle" fill={accent} fontSize="6" letterSpacing="2">THE WEDDING EDIT</text>
          <text x="50" y="46" textAnchor="middle" fill={accent} fontSize="20" fontFamily="serif">A&amp;D</text>
          <line x1="38" y1="52" x2="62" y2="52" stroke={accent} strokeWidth="0.6" />
        </svg>
      );
    case "cake":
      return (
        <svg viewBox="0 0 100 70" className={s} aria-hidden="true">
          <rect width="100" height="70" fill="#FFF5F1" />
          <rect x="32" y="48" width="36" height="14" fill={accent} opacity="0.4" />
          <rect x="38" y="34" width="24" height="14" fill={accent} opacity="0.55" />
          <rect x="44" y="22" width="12" height="12" fill={accent} opacity="0.7" />
          <path d="M 50 14 Q 47 10 44 13 Q 44 17 50 22 Q 56 17 56 13 Q 53 10 50 14" fill={accent} />
          {[10, 26, 74, 90].map((x, i) => <circle key={i} cx={x} cy={i % 2 ? 12 : 24} r="1.5" fill={accent} />)}
        </svg>
      );
    case "seal":
      return (
        <svg viewBox="0 0 100 70" className={s} aria-hidden="true">
          <rect width="100" height="70" fill="#0F0A0A" />
          <circle cx="50" cy="35" r="22" fill="none" stroke={accent} strokeWidth="1" />
          <circle cx="50" cy="35" r="16" fill="none" stroke={accent} strokeWidth="0.5" />
          <text x="50" y="40" textAnchor="middle" fill={accent} fontSize="14" fontFamily="serif" fontStyle="italic">A&amp;D</text>
        </svg>
      );
    default:
      return null;
  }
}

export default function IntroPicker({ value = "default", onChange, accent = "#B89B5E" }) {
  return (
    <div data-testid="intro-picker">
      <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2">Opening Animation</div>
      <div className="grid grid-cols-3 gap-2">
        {VARIANTS.map((v) => (
          <button
            type="button"
            key={v.id}
            onClick={() => onChange(v.id)}
            data-testid={`intro-variant-${v.id}`}
            className={`group text-left transition-all ${value === v.id ? "ring-2 ring-stone-900 ring-offset-2" : "hover:opacity-80"}`}
            title={v.subtitle}
          >
            <div className="aspect-[10/7] overflow-hidden border border-stone-200 bg-stone-50">
              <Thumb variant={v.id} accent={accent} />
            </div>
            <div className="text-[10px] uppercase tracking-[0.15em] mt-1 text-stone-700 truncate">{v.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// Re-export the variants list so other components (e.g. Admin) can use the labels
export const INTRO_VARIANTS = VARIANTS;
